import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Coffee } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false) {
    const dest = loc.state?.from || "/";
    nav(dest, { replace: true });
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      nav(loc.state?.from || "/", { replace: true });
    } else {
      setError(res.error || "Sign-in failed");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Hero */}
      <div className="hidden lg:flex relative overflow-hidden grain"
           style={{ background: "hsl(20 12% 10%)" }}>
        <img
          src="https://images.unsplash.com/photo-1743356629215-abb470447e71?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Coffee roastery"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-[hsl(36,25%,96%)] w-full">
          <img
            src="https://www.brotherscoffee.co.za/BCR_W%202026.png"
            alt="Brothers Coffee Roastery"
            className="h-16 w-auto self-start object-contain"
            style={{ maxWidth: "min(420px, 70%)" }}
          />
          <div className="max-w-md">
            <p className="label-tiny mb-3" style={{ color: "hsl(25 38% 62%)" }}>
              Manufacturing Suite
            </p>
            <h1 className="text-4xl md:text-5xl font-display font-bold leading-[1.05] tracking-tight">
              From green bean to <span style={{ color: "hsl(25 38% 62%)" }}>cost of sales</span> — without the spreadsheet.
            </h1>
            <p className="mt-5 text-base text-[hsl(36,25%,82%)] leading-relaxed">
              Daily roast sync, live FIFO inventory, and per-blend cost-per-kilogram —
              built for the team at the roastery in Great Brak River.
            </p>
          </div>
          <p className="text-xs" style={{ color: "hsl(25 8% 55%)" }}>
            © Brothers Coffee Roastery — Internal use
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Coffee className="w-5 h-5 text-primary" />
            <span className="font-display font-bold text-lg">Brothers Coffee</span>
          </div>
          <p className="label-tiny mb-2">Welcome back</p>
          <h2 className="text-3xl font-display font-bold mb-1 tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Use your roastery credentials.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-tiny mb-1.5 block" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-border rounded outline-none focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-tiny mb-1.5 block" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-border rounded outline-none focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-destructive" data-testid="login-error">{error}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full py-2.5 px-4 bg-primary text-primary-foreground rounded text-sm font-semibold tracking-wide hover:bg-[hsl(20,41%,32%)] transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <div className="flex justify-between text-xs pt-2">
              <a
                href="/forgot-password"
                className="text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
                data-testid="login-forgot-password-link"
              >Forgot password?</a>
              <a
                href="/forgot-email"
                className="text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
                data-testid="login-forgot-email-link"
              >Forgot your email?</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
